import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { Constants, fedTaxErrorMap, registrationTypes, stateCodes, stateTaxErrorMap, stateTypeIndicators, transactionDetailLabels } from '../../../shared/utilities/Constants';
import { createControl } from '../../../create-request/utilities/formUtilities';
import * as CustomValidators from '../../../shared/utilities/FormValidators';
import { selectNraIndicator, selectRegistrationType, selectStateCode } from '../../../account-info-header/reducers';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { percentAllowedChars } from '../../../shared/utilities/regex-patterns';
import { ValidateInputService } from '../../../shared/services/validate-input.service';
import { percentFormatter } from '../../../shared/utilities/PercentFormatter';
import { selectWireFedTaxPercent, selectWireStateCode, selectWireStateHasTaxes, selectWireStateTaxPercent, selectWireStateTypeIndicator } from '../../reducers';

import { FeatureFlagService } from '../../../shared/services/feature-flag-service';

@Component({
  standalone: false,
  selector: 'mm-sa-wire-tax-withholdings',
  templateUrl: './sa-wire-tax-withholdings.component.html',
  styleUrls: ['./sa-wire-tax-withholdings.component.scss']
})
export class SaWireTaxWithholdingsComponent implements OnInit {
  @Input() parentForm: UntypedFormGroup;
  taxWithholdingText: string = Constants.taxWithholdingText;
  nraText: string = Constants.nraText;
  nraIndicator: boolean;
  readonly fedTaxControl: string = 'fedTaxPercent';
  readonly stateTaxControl: string = 'stateTaxPercent';
  percentAllowedChars = percentAllowedChars;
  defaultFedTax: string;
  defaultStateTax: string;
  destroy$: Subject<boolean> = new Subject<boolean>();
  stateTypeIndicator: string;
  stateCode: string;
  regType: string;
  stateHasTaxes: boolean;
  saStateTaxLabel: string = transactionDetailLabels.stateTax;
  stateTaxAttentionRequiredWarningMessage: string = Constants.retirementStateTaxAttentionRequiredTextMA;

  constructor(private store: Store, public validationService: ValidateInputService, private featureFlagService: FeatureFlagService) { }

  ngOnInit(): void {
    this.store.select(selectWireFedTaxPercent).pipe(takeUntil(this.destroy$)).subscribe(fedTax => this.defaultFedTax = fedTax);
    this.store.select(selectWireStateTaxPercent).pipe(takeUntil(this.destroy$)).subscribe(stateTax => this.defaultStateTax = stateTax);

    createControl(this.parentForm, this.fedTaxControl, percentFormatter(this.defaultFedTax), [Validators.required, CustomValidators.zeroOrGreater, CustomValidators.wholeNumber, Validators.maxLength(10), CustomValidators.greaterThan99FedRequestCheck]);
    createControl(this.parentForm, this.stateTaxControl, percentFormatter(this.defaultStateTax), [Validators.required, CustomValidators.zeroOrGreater, Validators.maxLength(10), CustomValidators.greaterThan99State]);

    this.parentForm.setValidators(CustomValidators.sumDoesNotExceed99Point99('fedTaxPercent', 'stateTaxPercent'));

    this.store.select(selectNraIndicator).pipe(takeUntil(this.destroy$)).subscribe(_nraIndicator => this.nraIndicator = _nraIndicator ?? false);
    this.store.select(selectWireStateTypeIndicator).pipe(takeUntil(this.destroy$)).subscribe(_stateTypeIndicator => this.stateTypeIndicator = _stateTypeIndicator);
    this.store.select(selectWireStateCode).pipe(takeUntil(this.destroy$)).subscribe(_stateCode => this.stateCode = _stateCode);
    this.store.select(selectRegistrationType).pipe(takeUntil(this.destroy$)).subscribe(_regType => this.regType = _regType);
    this.store.select(selectWireStateHasTaxes).pipe(takeUntil(this.destroy$)).subscribe(_stateHasTaxes => this.stateHasTaxes = _stateHasTaxes);

    if (this.isEducationalSavings()) {
      this.fedTaxPercent.disable();
      this.stateTaxPercent.disable();
    }
    if (this.nraIndicator || !(this.isEducationalSavings() || this.stateHasTaxes)) {
      this.stateTaxPercent.disable();
    }

    this.store.select(selectStateCode).pipe(takeUntil(this.destroy$)).subscribe(stateCode => {
      if (stateCode && !this.nraIndicator) {
        this.saStateTaxLabel = `${transactionDetailLabels.stateTax} (${stateCode})`;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get fedTaxPercent() {
    return this.parentForm?.controls.fedTaxPercent;
  }

  get stateTaxPercent() {
    return this.parentForm?.controls.stateTaxPercent;
  }

  get isFedTaxInvalid(): boolean {
    return ((this.fedTaxPercent?.touched && this.fedTaxPercent?.invalid) || this.fedAndStateTaxSumError !== '');
  }

  get isStateTaxInvalid(): boolean {
    return ((this.stateTaxPercent?.touched && this.stateTaxPercent?.invalid) || this.fedAndStateTaxSumError !== '');
  }

  onFedTaxBlur(): void {
    if (this.fedTaxPercent.valid) {
      this.fedTaxPercent?.setValue(percentFormatter(this.fedTaxPercent.value));
    }
  }

  onStateTaxBlur(): void {
    if (this.stateTaxPercent.valid) {
      this.stateTaxPercent?.setValue(percentFormatter(this.stateTaxPercent.value));
    }
  }

  get fedTaxError() {
    if (this.fedTaxPercent && this.fedTaxPercent.errors) {
      const errorKeys: string[] = Object.keys(this.fedTaxPercent.errors ?? {});
      if (errorKeys.length > 0) {
        return fedTaxErrorMap[errorKeys[0] as keyof typeof fedTaxErrorMap];
      }
    }
  }

  get stateTaxError() {
    if (this.stateTaxPercent && this.stateTaxPercent.errors) {
      const errorKeys: string[] = Object.keys(this.stateTaxPercent.errors ?? {});
      if (errorKeys.length > 0) {
        return stateTaxErrorMap[errorKeys[0] as keyof typeof stateTaxErrorMap];
      }
    }
  }

  get fedAndStateTaxSumError(): string {
    if (this.parentForm && this.parentForm.errors) {
      const errorKeys: string[] = Object.keys(
        this.parentForm.errors ?? {}
      );
      if (errorKeys.length > 0) {
        return 'Total tax withholding cannot exceed 99.99%. Update values or request a one-time check to withhold 100%';
      }
    }
    return '';
  }

  get showMassachusettsDistributionWarning(): boolean {
    return this.featureFlagService.featureFlags.enableSpecialStateTax && this.stateCode?.toUpperCase() === stateCodes.Massachusetts;
  }

  isEducationalSavings(): boolean {
    return this.regType === registrationTypes.educationalSavings;
  }
}
